import { utils } from '@atlaskit/util-service-support/constants';
import { RequestServiceOptions } from '@atlaskit/util-service-support/types';
import { captureException } from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';

import { MAX_FREQUENT_OBJECTS } from './constants';
import {
	CollabGraphContainerResponse,
	CollaborationGraphContainer,
	CollaborationGraphContainerRequestBody,
	CollaborationGraphObject,
	CollaborationGraphObjectRequestBody,
	CollaborationGraphObjectResponse,
	Context,
	supportedCollabGraphObjectProducts,
} from './types';

type getCollabGraphContainersResult = {
	data: CollaborationGraphContainer[];
	error: boolean;
};

type getCollabGraphObjectsResult = {
	data: CollaborationGraphObject[];
	error: boolean;
};

export default class CollaborationGraphProvider {
	private baseUrl: string;
	private containerPromise?: Promise<CollabGraphContainerResponse>;
	private objectPromise?: Promise<CollaborationGraphObjectResponse>;

	constructor(baseUrl: string) {
		this.baseUrl = baseUrl;
	}

	async getCollabGraphContainers(
		userId: string,
		siteIds: string[],
	): Promise<getCollabGraphContainersResult> {
		if (!this.containerPromise) {
			const requestBody: CollaborationGraphContainerRequestBody = {
				containerTypes: ['confluenceSpace', 'jiraProject'],
				context: {
					contextType: 'team-central',
					principalId: userId,
					siteIds,
				},
				expanded: true,
				maxNumberOfResults: 10,
				userId,
			};
			const options: RequestServiceOptions = {
				requestInit: {
					mode: 'cors',
					method: 'post',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(requestBody),
				},
			};

			this.containerPromise = utils.requestService(
				{ url: `${this.baseUrl}/v1/collaborationgraph/multitenant/user/container` },
				options,
			);
		}

		const defaultResponse = { data: [], error: false };

		try {
			const response = await this.containerPromise;

			if (!response.collaborationGraphEntities) {
				return defaultResponse;
			}

			return {
				data: response.collaborationGraphEntities,
				error: false,
			};
		} catch (error) {
			captureException(error, {
				tags: { source: 'CollaborationGraphProvider', method: 'getCollabGraphContainers' },
			});
			return {
				data: [],
				error: true,
			};
		}
	}

	async getCollabGraphObjects(
		userId: string,
		siteIds: string[],
	): Promise<getCollabGraphObjectsResult> {
		if (!this.objectPromise) {
			const contexts: Context[] = siteIds.flatMap((tenantId) =>
				supportedCollabGraphObjectProducts.map((product) => {
					return {
						userId,
						tenantId,
						product,
					};
				}),
			);

			const objectsRequestBody: CollaborationGraphObjectRequestBody = {
				contexts,
				modelRequestParams: {
					caller: 'team-central',
					experience: 'StartQuickAccess',
				},
				requestingUserId: userId,
				sessionId: uuidv4(),
				expand: 'existing',
				maxNumberOfResults: MAX_FREQUENT_OBJECTS,
			};

			const options: RequestServiceOptions = {
				requestInit: {
					mode: 'cors',
					method: 'post',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(objectsRequestBody),
				},
			};

			this.objectPromise = utils.requestService(
				{ url: `${this.baseUrl}/v2/recommend/bulk/object` },
				options,
			);
		}

		const defaultResponse = { data: [], error: false };

		try {
			const response = await this.objectPromise;

			if (!response?.recommendedEntities) {
				return defaultResponse;
			}

			return {
				data: response.recommendedEntities,
				error: false,
			};
		} catch (error) {
			captureException(error, {
				tags: { source: 'CollaborationGraphProvider', method: 'getCollabGraphObjects' },
			});
			return {
				data: [],
				error: true,
			};
		}
	}
}
